import React from "react";
import { Link, Route } from "react-router-dom";

import NotFound from "../components/NotFound";
import RequireAuth from "../components/RequireAuth";
import { AUTH_LEVELS } from "../lib/globals";
import Contact, { loader as ContactLoader } from "../pages/Index/Contact";
import Field, { loader as FieldLoader } from "../pages/Index/Field";
import Fields, { loader as FieldsLoader } from "../pages/Index/Fields";
import History from "../pages/Index/History";
import HomePage, { loader as HomePageLoader } from "../pages/Index/HomePage";
import Login from "../pages/Index/Login";
import LookingForPlayers, {
    loader as LookingForPlayersLoader,
} from "../pages/Index/LookingForPlayers";
import LostPassword, {
    action as LostPasswordAction,
} from "../pages/Index/LostPassword";
import PhotoUpload from "../pages/Index/PhotoUpload";
import Profile, { loader as ProfileLoader } from "../pages/Index/Profile";
import Register, { loader as RegisterLoader } from "../pages/Index/Register";
import RegisterTeam, {
    loader as RegisterTeamLoader,
} from "../pages/Index/RegisterTeam";
import ResetPassword, {
    loader as ResetPasswordLoader,
} from "../pages/Index/ResetPassword";
import Rules, { loader as RulesLoader } from "../pages/Index/Rules";
import Sponsor from "../pages/Index/Sponsor";
import Testimonials from "../pages/Index/Testimonials";
import ThankYou, { loader as ThankYouLoader } from "../pages/Index/ThankYou";
import Tryouts, { loader as TryoutsLoader } from "../pages/Index/Tryouts";
import Waiver, { loader as WaiverLoader } from "../pages/Index/Waiver";
import { IAPIResponse, IField } from "../types/api";
import { IUserProfileData } from "../types/forms";

export default (
    <Route>
        <Route
            index
            element={<HomePage />}
            loader={HomePageLoader}
            handle={{ noHeader: true }}
        />
        <Route
            path="history"
            element={<History />}
            handle={{
                crumb: () => <Link to="/history">History of MAJOR YBL</Link>,
                header: () => "History of MAJOR YBL",
            }}
        />
        <Route
            path="sponsor"
            element={<Sponsor />}
            handle={{
                crumb: () => (
                    <Link to="/sponsor">
                        Become an Official Sponsor of the MAJOR YBL
                    </Link>
                ),
                header: () => "Become an Official Sponsor of the MAJOR YBL",
            }}
        />
        <Route
            path="contact-us"
            element={<Contact />}
            loader={ContactLoader}
            handle={{
                crumb: () => (
                    <Link to="/contact-us">Contact the MAJORY YBL</Link>
                ),
                header: () => "Contact Us MAJOR YBL",
            }}
        />
        <Route
            path="photo-upload"
            element={<PhotoUpload />}
            handle={{
                crumb: () => <Link to="/photo-upload">Photo Upload</Link>,
                header: () => "Photo Upload",
            }}
        />
        <Route
            path="testimonials"
            element={<Testimonials />}
            handle={{
                crumb: () => (
                    <Link to="/testimonials">What Others Have to Say...</Link>
                ),
                header: () => "What others have to say about the MAJOR YBL",
            }}
        />
        <Route path="rules" handle={{ crumb: () => "Rules" }}>
            <Route
                path="majorybl"
                element={<Rules />}
                loader={params =>
                    RulesLoader({
                        ...params,
                        content: "majorybl_rules_page_content",
                    })
                }
                handle={{
                    crumb: () => (
                        <Link to="/rules/majorybl">MAJOR YBL Rules</Link>
                    ),
                    header: () => "MAJORYBL Rules",
                }}
            />
            <Route
                path="majorhighschool"
                element={<Rules />}
                loader={params =>
                    RulesLoader({
                        ...params,
                        content: "majorybl_highschool_rules_page_content",
                    })
                }
                handle={{
                    crumb: () => (
                        <Link to="/rules/majorhighschool">
                            MAJOR High School Rules
                        </Link>
                    ),
                    header: () => "MAJOR High School Rules",
                }}
            />
        </Route>
        <Route
            path="fields"
            handle={{
                crumb: () => <Link to="/fields">League Fields</Link>,
            }}
        >
            <Route
                index
                element={<Fields />}
                loader={FieldsLoader}
                handle={{
                    header: () => "League Fields",
                }}
            />
            <Route
                path=":id"
                element={<Field />}
                loader={FieldLoader}
                handle={{
                    crumb: ({
                        field: { field: data },
                    }: {
                        field: IAPIResponse & { field: IField };
                    }) => <Link to={`/fields/${data.id}`}>{data.name}</Link>,
                    header: ({
                        field: { field },
                    }: {
                        field: IAPIResponse & { field: IField };
                    }) => field.name,
                }}
            />
        </Route>
        <Route
            path="tryouts"
            element={<Tryouts />}
            loader={TryoutsLoader}
            handle={{
                crumb: () => <Link to="/tryouts">Tryout Info</Link>,
                header: () => "Upcoming Tryouts",
            }}
        />
        <Route
            path="looking-for-players"
            element={<LookingForPlayers />}
            loader={LookingForPlayersLoader}
            handle={{
                crumb: () => <Link to="/looking-for-players">Join a Team</Link>,
                header: () => "Join a Team",
            }}
        />
        <Route
            path="login"
            element={<Login />}
            handle={{
                crumb: () => <Link to="/login">Login</Link>,
                header: () => "Login",
            }}
        />
        <Route
            path="lost-password"
            element={<LostPassword />}
            action={LostPasswordAction}
            handle={{
                crumb: () => <Link to="/lost-password">Lost Password</Link>,
                header: () => "Lost Password",
            }}
        />
        <Route
            path="reset-password"
            element={<ResetPassword />}
            loader={ResetPasswordLoader}
            handle={{
                crumb: () => <Link to="/reset-password">Reset Password</Link>,
                header: () => "Reset Password",
            }}
        />
        <Route element={<RequireAuth allowedUserLevels={AUTH_LEVELS.all} />}>
            <Route
                path="register/team"
                element={<RegisterTeam />}
                loader={RegisterTeamLoader}
                handle={{
                    crumb: () => <Link to="/register/team">Register Team</Link>,
                    header: () => "Register Team",
                }}
            />
        </Route>
        <Route
            path="register/:userType"
            element={<Register />}
            loader={RegisterLoader}
            handle={{
                crumb: ({ userType }: { userType: string }) => {
                    let crumbDisplay;

                    switch (userType) {
                        case "coach":
                            crumbDisplay = "Coach";
                            break;
                        case "orguser":
                            crumbDisplay = "Organizational User";
                            break;
                    }

                    return (
                        <Link to={`/register/${crumbDisplay}`}>
                            Register {crumbDisplay}
                        </Link>
                    );
                },
                header: ({ userType }: { userType: string }) => {
                    let headerDisplay;
                    switch (userType) {
                        case "coach":
                            headerDisplay =
                                "THE MAJOR YBL, MAJOR HIGH SCHOOL LEAGUE AND MAJOR FALL LEAGUE COACH REGISTRATION";
                            break;
                        case "orguser":
                            headerDisplay =
                                "MAJORYBL Organizational User Registration";
                            break;
                    }
                    return headerDisplay;
                },
            }}
        />
        <Route element={<RequireAuth allowedUserLevels={AUTH_LEVELS.all} />}>
            <Route
                path="profile"
                element={<Profile />}
                loader={ProfileLoader}
                handle={{
                    crumb: () => <Link to="profile">My Profile</Link>,
                    header: () => "My Profile",
                }}
            />
        </Route>
        <Route
            element={
                <RequireAuth allowedUserLevels={AUTH_LEVELS.leaguemanagement} />
            }
        >
            <Route
                path="profile/:userId"
                element={<Profile />}
                loader={ProfileLoader}
                handle={{
                    crumb: (data: IUserProfileData) => (
                        <Link to={`profile/${data.id}`}>Edit User Profile</Link>
                    ),
                }}
            />
        </Route>
        <Route
            path="thankyou"
            element={<ThankYou />}
            loader={ThankYouLoader}
            handle={{
                crumb: () => <Link to={`thankyou`}>Checkout Complete</Link>,
                header: () => "Checkout Complete",
            }}
        />
        <Route
            path="waiver/:playerId"
            element={<Waiver />}
            loader={WaiverLoader}
            handle={{
                crumb: () => "Player Waiver Acceptance",
                header: () => "Player Waiver Acceptance",
            }}
        />
        <Route path="*" element={<NotFound parent="index" />} />
    </Route>
);
